import React, { useState, useCallback, useRef } from "react";
import { Keyboard } from "react-native";

import type {
  ChatInputHandle,
  ThemeMode,
  ResearchState,
} from "./ChatInputView";

import ChatInputView from "./ChatInputView";
import {
  sendOnEnter,
  type EnterToSendKeyPressEvent,
} from "../../../utils/enterToSend";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Suggested question configuration for chat input
 */
interface SuggestedQuestion {
  icon: string;
  text: string;
  query: string;
}

/**
 * Container component for chat input with suggested questions
 */
interface ChatInputContainerProps {
  onSend: (text: string, researchMode: boolean) => void;
  onMessageSent?: () => void;
  canSend: boolean;
  isResearchMode: boolean;
  onToggleResearch: () => void;
  isResearchToggleEnabled: boolean;
  placeholder?: string;
  showSuggestedQuestions?: boolean;
  suggestedQuestions?: SuggestedQuestion[];
  onToggleSuggestedQuestions?: () => void;
  /**
   * Focus the input on mount (mobile only — used when arriving from the
   * Home Ask Gemini Pressable so the keyboard opens immediately).
   */
  autoFocus?: boolean;
}

/**
 * Container component for chat input functionality
 * @param root0 - Component props
 * @param root0.onSend - Callback invoked when user sends a message with text and research mode state
 * @param root0.onMessageSent - Optional callback invoked after message is sent successfully
 * @param root0.canSend - Whether sending messages is currently allowed
 * @param root0.isResearchMode - Whether deep research mode is currently enabled
 * @param root0.onToggleResearch - Callback invoked when research mode toggle is pressed
 * @param root0.isResearchToggleEnabled - Whether research mode toggle button should be displayed
 * @param root0.placeholder - Optional placeholder text for input field
 * @param root0.showSuggestedQuestions - Whether suggested questions panel is currently expanded
 * @param root0.suggestedQuestions - Array of suggested question objects to display
 * @param root0.onToggleSuggestedQuestions - Callback invoked when suggested questions toggle is pressed
 * @param root0.autoFocus - Focus input on mount (mobile-only entrypoint from Home Ask Gemini)
 */
const ChatInputContainer: React.FC<ChatInputContainerProps> = ({
  onSend,
  onMessageSent,
  canSend,
  isResearchMode,
  onToggleResearch,
  isResearchToggleEnabled,
  placeholder,
  showSuggestedQuestions,
  suggestedQuestions,
  onToggleSuggestedQuestions,
  autoFocus,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [text, setText] = useState("");
  const inputRef = useRef<ChatInputHandle>(null);

  /**
   * Sends the draft and settles the keyboard.
   * @param fromKeyboard - True for an Enter-send: the input keeps focus so a
   * keyboard user can keep typing. The Send button dismisses the keyboard,
   * which on touch hides the soft keyboard on purpose.
   */
  const sendDraft = useCallback(
    (fromKeyboard: boolean) => {
      if (!canSend || !text.trim()) return;
      onSend(text, isResearchMode);
      setText("");
      if (!fromKeyboard) Keyboard.dismiss();
      onMessageSent?.();
      // The parent's send path (ChatContainer.onSendText) dismisses the
      // keyboard too, so focus is handed back after it rather than merely
      // not taken away here.
      if (fromKeyboard) inputRef.current?.focus();
    },
    [canSend, text, onSend, isResearchMode, onMessageSent]
  );

  const handleSend = useCallback(() => sendDraft(false), [sendDraft]);

  // SE-8646: on a web keyboard Enter sends and Shift+Enter keeps its newline.
  const handleKeyPress = useCallback(
    (event: EnterToSendKeyPressEvent) =>
      sendOnEnter(event, () => sendDraft(true)),
    [sendDraft]
  );

  const handleSuggestedQuestionPress = useCallback(
    (query: string) => {
      onSend(query, isResearchMode);
      onMessageSent?.();
    },
    [onSend, isResearchMode, onMessageSent]
  );

  // Stable per-question handler, so no JSX attribute in the View carries an
  // inline closure (SE-8019).
  const createQuestionPressHandler = useCallback(
    (query: string) => () => handleSuggestedQuestionPress(query),
    [handleSuggestedQuestionPress]
  );

  const themeMode: ThemeMode = isDark ? "dark" : "light";

  const researchState: ResearchState = isResearchMode ? "active" : "inactive";

  return (
    <ChatInputView
      value={text}
      onChangeText={setText}
      onSend={handleSend}
      onKeyPress={handleKeyPress}
      inputRef={inputRef}
      canSend={canSend && text.trim().length > 0}
      isDark={isDark}
      isResearchMode={isResearchMode}
      onToggleResearch={onToggleResearch}
      isResearchToggleEnabled={isResearchToggleEnabled}
      placeholder={placeholder}
      showSuggestedQuestions={showSuggestedQuestions}
      suggestedQuestions={suggestedQuestions}
      createQuestionPressHandler={createQuestionPressHandler}
      onToggleSuggestedQuestions={onToggleSuggestedQuestions}
      autoFocus={autoFocus}
      themeMode={themeMode}
      researchState={researchState}
    />
  );
};

export default ChatInputContainer;
